import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../api';
import {
  Upload,
  FileJson,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  AlertTriangle,
  FileText,
  Layers,
  ArrowRight,
} from 'lucide-react';
import clsx from 'clsx';

interface UpdateConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  currentConfig: object;
}

interface ParsedConfig {
  site?: {
    shell?: {
      theme?: string;
      brand?: { title?: string };
    };
  };
  pages?: Array<{
    id: string;
    label?: string;
    enabled?: boolean;
    sections?: Array<{ sectionId: string }>;
  }>;
}

interface ConfigDiff {
  pagesAdded: string[];
  pagesRemoved: string[];
  pagesModified: string[];
  sectionsCountBefore: number;
  sectionsCountAfter: number;
  themeChanged: boolean;
  themeBefore: string;
  themeAfter: string;
}

export function UpdateConfigModal({ 
  isOpen, 
  onClose, 
  projectId, 
  projectName,
  currentConfig 
}: UpdateConfigModalProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [step, setStep] = useState<'upload' | 'preview'>('upload');
  const [rawJson, setRawJson] = useState<string>('');
  const [parsedConfig, setParsedConfig] = useState<ParsedConfig | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [diff, setDiff] = useState<ConfigDiff | null>(null);

  // Mutation per aggiornare la config
  const updateMutation = useMutation({
    mutationFn: (config: object) => 
      projectsApi.update(projectId, { config, changeDescription: 'Configurazione importata da file' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project-history', projectId] });
      onClose();
      resetState();
    },
  });

  const resetState = () => {
    setStep('upload');
    setRawJson('');
    setParsedConfig(null);
    setParseError(null);
    setDiff(null);
  };

  const handleClose = () => {
    onClose();
    resetState();
  };

  const countSections = (config: ParsedConfig): number => {
    return (config.pages || []).reduce((acc, page) => acc + (page.sections?.length || 0), 0);
  };

  const getPageIds = (config: ParsedConfig): string[] => {
    return (config.pages || []).map(p => p.id);
  };

  const getTheme = (config: ParsedConfig): string => {
    return config.site?.shell?.theme || 'default';
  };

  const computeDiff = (before: ParsedConfig, after: ParsedConfig): ConfigDiff => {
    const pageIdsBefore = new Set(getPageIds(before));
    const pageIdsAfter = new Set(getPageIds(after));
    
    const pagesAdded = [...pageIdsAfter].filter(id => !pageIdsBefore.has(id));
    const pagesRemoved = [...pageIdsBefore].filter(id => !pageIdsAfter.has(id));
    
    // Pagine modificate: presenti in entrambi ma con contenuto diverso
    const pagesModified: string[] = [];
    for (const id of pageIdsAfter) {
      if (pageIdsBefore.has(id)) {
        const pageBefore = (before.pages || []).find(p => p.id === id);
        const pageAfter = (after.pages || []).find(p => p.id === id);
        if (JSON.stringify(pageBefore) !== JSON.stringify(pageAfter)) {
          pagesModified.push(id);
        }
      }
    }

    const themeBefore = getTheme(before);
    const themeAfter = getTheme(after);

    return {
      pagesAdded,
      pagesRemoved,
      pagesModified,
      sectionsCountBefore: countSections(before),
      sectionsCountAfter: countSections(after),
      themeChanged: themeBefore !== themeAfter,
      themeBefore,
      themeAfter,
    };
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      processJsonContent(content);
    };
    reader.onerror = () => {
      setParseError('Errore nella lettura del file');
    };
    reader.readAsText(file);
  };

  const handlePaste = () => {
    if (rawJson.trim()) {
      processJsonContent(rawJson);
    }
  };

  const processJsonContent = (content: string) => {
    setParseError(null);
    
    try {
      const parsed = JSON.parse(content);
      
      if (typeof parsed !== 'object' || parsed === null) {
        throw new Error('Il JSON deve essere un oggetto');
      }

      if (!parsed.pages && !parsed.site) {
        throw new Error('La configurazione deve contenere almeno "site" o "pages"');
      }

      setParsedConfig(parsed);
      setRawJson(content);
      
      // Calcola differenze
      const configDiff = computeDiff(currentConfig as ParsedConfig, parsed);
      setDiff(configDiff);
      
      setStep('preview');
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'JSON non valido');
    }
  };

  const handleUpdate = () => {
    if (!parsedConfig) return;
    updateMutation.mutate(parsedConfig);
  };

  const hasChanges = diff && (
    diff.pagesAdded.length > 0 ||
    diff.pagesRemoved.length > 0 ||
    diff.pagesModified.length > 0 ||
    diff.themeChanged ||
    diff.sectionsCountBefore !== diff.sectionsCountAfter
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Upload className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Aggiorna configurazione
              </h2>
              <p className="text-sm text-gray-500">{projectName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Warning */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Attenzione</p>
                  <p className="text-sm text-amber-700">
                    L'importazione sostituirà completamente la configurazione attuale.
                    Una copia verrà salvata nello storico versioni.
                  </p>
                </div>
              </div>

              {/* File upload */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className={clsx(
                  'border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors',
                  'hover:border-primary-400 hover:bg-primary-50/50',
                  'border-gray-300 bg-gray-50'
                )}
              >
                <FileJson className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="font-medium text-gray-700">
                  Clicca per selezionare un file JSON
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  oppure trascina qui il file
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500">oppure</span>
                </div>
              </div>

              {/* Paste JSON */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Incolla il JSON
                </label>
                <textarea
                  value={rawJson}
                  onChange={(e) => setRawJson(e.target.value)}
                  placeholder='{"site": {...}, "pages": [...]}'
                  className="input font-mono text-sm h-32"
                />
                <button
                  onClick={handlePaste}
                  disabled={!rawJson.trim()}
                  className="btn-primary mt-3 w-full"
                >
                  Analizza JSON
                </button>
              </div>

              {/* Error */}
              {parseError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-800">Errore nel parsing</p>
                    <p className="text-sm text-red-600">{parseError}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview diff */}
          {step === 'preview' && diff && (
            <div className="space-y-6">
              {/* Success parse message */}
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-green-800 font-medium">
                  Configurazione valida
                </p>
              </div>

              {/* Changes summary */}
              {hasChanges ? (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Modifiche rilevate</h3>
                  
                  {/* Theme change */}
                  {diff.themeChanged && (
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-gray-600">Tema:</span>
                      <span className="font-mono bg-red-100 text-red-700 px-2 py-0.5 rounded">
                        {diff.themeBefore}
                      </span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                      <span className="font-mono bg-green-100 text-green-700 px-2 py-0.5 rounded">
                        {diff.themeAfter}
                      </span>
                    </div>
                  )}

                  {/* Sections count */}
                  {diff.sectionsCountBefore !== diff.sectionsCountAfter && (
                    <div className="flex items-center space-x-2 text-sm">
                      <Layers className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">Sezioni:</span>
                      <span className="font-mono">{diff.sectionsCountBefore}</span>
                      <ArrowRight className="w-4 h-4 text-gray-400" />
                      <span className="font-mono">{diff.sectionsCountAfter}</span>
                    </div>
                  )}

                  {/* Pages changes */}
                  {diff.pagesAdded.length > 0 && (
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-green-800 font-medium mb-1">
                        <FileText className="w-4 h-4" />
                        <span>Pagine aggiunte ({diff.pagesAdded.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {diff.pagesAdded.map(id => (
                          <span key={id} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {diff.pagesRemoved.length > 0 && (
                    <div className="p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-red-800 font-medium mb-1">
                        <FileText className="w-4 h-4" />
                        <span>Pagine rimosse ({diff.pagesRemoved.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {diff.pagesRemoved.map(id => (
                          <span key={id} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {diff.pagesModified.length > 0 && (
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <div className="flex items-center space-x-2 text-sm text-amber-800 font-medium mb-1">
                        <FileText className="w-4 h-4" />
                        <span>Pagine modificate ({diff.pagesModified.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {diff.pagesModified.map(id => (
                          <span key={id} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                  <p className="text-gray-600">
                    Nessuna differenza rilevata tra la configurazione attuale e quella importata.
                  </p>
                </div>
              )}

              {/* Confirmation warning */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Confermi l'aggiornamento?</strong> La configurazione attuale verrà 
                  salvata nello storico e potrà essere ripristinata in seguito.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
          <div>
            {step === 'preview' && (
              <button
                onClick={() => setStep('upload')}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Torna indietro
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button onClick={handleClose} className="btn-secondary">
              Annulla
            </button>
            {step === 'preview' && (
              <button
                onClick={handleUpdate}
                disabled={updateMutation.isPending}
                className="btn-primary"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Aggiornamento...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Conferma aggiornamento
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Error from mutation */}
        {updateMutation.isError && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-200 flex items-center space-x-2 text-red-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">
              Errore nell'aggiornamento: {(updateMutation.error as Error)?.message}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
